'use client';

/**
 * src/app/(public)/penal/page.js
 *
 * FASE 3 Feature #5 — Landing pública GPTPenal.
 *
 * SIN AUTH. Cualquier ciudadano puede consultar el nuevo Código Penal Ley 74-25.
 * Funnel hacia registro Dr. Víctor cuando el user agota cuota o quiere más.
 */

import { useState } from 'react';
import {
  Scales, Question, Sparkle, PaperPlaneTilt, ArrowSquareOut, Spinner, Lock,
} from '@phosphor-icons/react/dist/ssr';
import TurnstileWidget from '@/components/TurnstileWidget';

const EJEMPLOS = [
  '¿Cuáles son las penas por robo agravado en el nuevo Código Penal?',
  '¿Qué cambió con la Ley 74-25 sobre prisión preventiva?',
  '¿Cuándo procede la suspensión condicional de la pena?',
  '¿Qué es el principio de oportunidad?',
];

export default function PenalClient() {
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  // Turnstile (audit coverage 2026-05-25). Endpoint público sin auth →
  // protección anti-botnet. Si la env var no está, el widget no renderiza
  // y el backend hace skip.
  const [captchaToken, setCaptchaToken] = useState('');
  const TURNSTILE_SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || '';

  async function handleSubmit(e) {
    e?.preventDefault();
    if (!question.trim() || loading) return;

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const res = await fetch('/api/penal/ask', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: question.trim(),
          captcha_token: captchaToken,
        }),
      });
      const data = await res.json();

      if (!res.ok) {
        setError({
          message: data.error || 'Error procesando consulta',
          code: data.code,
          signup_url: data.signup_url,
        });
      } else {
        setResult(data);
      }
    } catch (err) {
      setError({ message: 'Error de red. Reintenta.' });
    } finally {
      setLoading(false);
    }
  }

  return (
    <main style={containerStyle}>
      {/* Hero */}
      <section style={heroStyle}>
        <div style={brandStyle}>
          <Scales size={36} weight="duotone" color="#B8860B" />
          <h1 style={titleStyle}>GPTPenal</h1>
        </div>
        <p style={subtitleStyle}>
          Consulta el <strong>nuevo Código Penal RD</strong> (Ley 74-25) en lenguaje claro.
        </p>
        <p style={metaStyle}>
          <Sparkle size={14} weight="fill" color="#B8860B" /> Gratis · Sin registro · 3 consultas/día
        </p>
      </section>

      {/* Form */}
      <form onSubmit={handleSubmit} style={formStyle}>
        <textarea
          style={textareaStyle}
          placeholder="Escribe tu consulta sobre el Código Penal RD..."
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          rows={3}
          maxLength={500}
          disabled={loading}
        />
        <div style={formFooterStyle}>
          <span style={{ fontSize: 11, color: '#94a3b8' }}>
            {question.length}/500
          </span>
          <button
            type="submit"
            disabled={loading || !question.trim() || (!!TURNSTILE_SITE_KEY && !captchaToken)}
            style={submitBtnStyle}
          >
            {loading ? (
              <><Spinner size={14} className="animate-spin" /> Procesando...</>
            ) : TURNSTILE_SITE_KEY && !captchaToken ? (
              <><Spinner size={14} className="animate-spin" /> Validando seguridad...</>
            ) : (
              <><PaperPlaneTilt size={14} weight="fill" /> Consultar</>
            )}
          </button>
        </div>
        {TURNSTILE_SITE_KEY && (
          <div style={{ marginTop: 12, display: 'flex', justifyContent: 'center' }}>
            <TurnstileWidget
              action="penal-ask"
              onToken={setCaptchaToken}
              theme="light"
              size="compact"
            />
          </div>
        )}
      </form>

      {/* Ejemplos */}
      {!result && !error && (
        <div style={ejemplosStyle}>
          <p style={{ fontSize: 12, color: '#57534e', margin: '0 0 8px' }}>
            ¿No sabes por dónde empezar? Prueba:
          </p>
          {EJEMPLOS.map((ej, i) => (
            <button
              key={i}
              style={ejemploBtnStyle}
              onClick={() => setQuestion(ej)}
              disabled={loading}
            >
              <Question size={12} weight="bold" /> {ej}
            </button>
          ))}
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={errorBoxStyle}>
          <p style={{ margin: 0, fontSize: 14, color: '#991B1B' }}>⚠️ {error.message}</p>
          {error.signup_url && (
            <a href={error.signup_url} style={ctaLinkStyle}>
              <Lock size={14} weight="bold" /> Regístrate gratis para más consultas →
            </a>
          )}
        </div>
      )}

      {/* Resultado */}
      {result && (
        <div style={resultStyle}>
          {result.cached && (
            <div style={cachedBadgeStyle}>
              <Sparkle size={12} weight="fill" color="#10b981" /> Pregunta frecuente · Respuesta verificada
            </div>
          )}
          <h3 style={{ fontSize: 16, margin: '0 0 12px', color: '#0F172A' }}>
            {question}
          </h3>
          <div style={answerStyle}>
            {result.answer.split('\n').map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
          {result.citation && (
            <p style={citationStyle}>📖 {result.citation}</p>
          )}
          <p style={disclaimerStyle}>
            {result.disclaimer}
          </p>
        </div>
      )}

      {/* Funnel a registro */}
      <section style={funnelStyle}>
        <h3 style={{ fontSize: 16, color: '#0F172A', margin: '0 0 8px' }}>
          ¿Necesitas más que consultas básicas?
        </h3>
        <p style={{ fontSize: 13, color: '#57534e', marginBottom: 12 }}>
          Dr. Víctor es el OS del despacho legal: <strong>plazos automáticos</strong>,
          <strong> análisis casacional</strong>, <strong>jurisprudencia con cite-to-verify</strong>,
          <strong> 19 personalidades legales</strong>. Pruébalo gratis 3 días.
        </p>
        <a href="/signup" style={ctaLinkStyle}>
          <ArrowSquareOut size={14} weight="bold" /> Probar Dr. Víctor gratis →
        </a>
      </section>

      <footer style={footerStyle}>
        <p>
          <strong>Dr. Víctor</strong> · El OS del despacho legal RD ·{' '}
          <a href="https://drvictorlegal.com" style={{ color: '#B8860B' }}>drvictorlegal.com</a>
        </p>
        <p style={{ fontSize: 10, color: '#94a3b8' }}>
          GPTPenal es una herramienta educativa sobre la Ley 74-25. No sustituye consulta con abogado matriculado CARD.
        </p>
      </footer>
    </main>
  );
}

// Styles
const containerStyle = {
  maxWidth: 720, margin: '0 auto',
  padding: '32px 24px 40px',
  background: '#fdfaf2', minHeight: '100vh',
};
const heroStyle = { textAlign: 'center', marginBottom: 24 };
const brandStyle = { display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'center', marginBottom: 8 };
const titleStyle = { fontSize: 32, margin: 0, color: '#0F172A', fontWeight: 800 };
const subtitleStyle = { fontSize: 15, color: '#57534e', margin: '0 0 6px' };
const metaStyle = { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 12, color: '#78716c', margin: 0 };
const formStyle = { background: '#fff', borderRadius: 12, padding: 16, border: '1px solid rgba(184, 134, 11, 0.2)', marginBottom: 16 };
const textareaStyle = {
  width: '100%', border: 'none', resize: 'vertical', fontFamily: 'inherit',
  fontSize: 14, color: '#0F172A', outline: 'none', padding: '8px 0',
};
const formFooterStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 };
const submitBtnStyle = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  background: '#B8860B', color: '#fff', border: 'none',
  borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
};
const ejemplosStyle = { background: '#fff', borderRadius: 10, padding: 12, border: '1px dashed rgba(184, 134, 11, 0.3)' };
const ejemploBtnStyle = {
  display: 'flex', alignItems: 'center', gap: 6,
  width: '100%', textAlign: 'left',
  background: 'transparent', border: 'none', cursor: 'pointer',
  padding: '6px 8px', borderRadius: 6, fontSize: 12, color: '#57534e',
  fontFamily: 'inherit',
};
const errorBoxStyle = {
  background: 'rgba(239, 68, 68, 0.06)', border: '1px solid rgba(239, 68, 68, 0.25)',
  borderRadius: 10, padding: 14, marginTop: 8,
};
const ctaLinkStyle = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  marginTop: 10, color: '#B8860B', fontWeight: 600, fontSize: 13, textDecoration: 'none',
};
const resultStyle = { background: '#fff', borderRadius: 12, padding: 18, border: '1px solid rgba(184, 134, 11, 0.15)', marginTop: 8 };
const cachedBadgeStyle = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  background: 'rgba(16, 185, 129, 0.1)', color: '#065F46',
  fontSize: 11, fontWeight: 600,
  padding: '3px 8px', borderRadius: 4, marginBottom: 10,
};
const answerStyle = { fontSize: 14, lineHeight: 1.6, color: '#0F172A' };
const citationStyle = {
  background: 'rgba(184, 134, 11, 0.06)', borderLeft: '3px solid #B8860B',
  padding: '8px 12px', borderRadius: '0 6px 6px 0', fontSize: 13, color: '#78350F', margin: '12px 0',
};
const disclaimerStyle = { fontSize: 11, color: '#78716c', fontStyle: 'italic', borderTop: '1px solid #f1f5f9', paddingTop: 10, marginTop: 10 };
const funnelStyle = {
  background: 'linear-gradient(135deg, rgba(184, 134, 11, 0.08), rgba(184, 134, 11, 0.04))',
  border: '1px solid rgba(184, 134, 11, 0.2)',
  borderRadius: 12, padding: 20, marginTop: 20, textAlign: 'center',
};
const footerStyle = {
  marginTop: 30, paddingTop: 16,
  borderTop: '1px solid rgba(148, 163, 184, 0.2)',
  textAlign: 'center', fontSize: 12, color: '#78716c',
};
